import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsService } from '../../services/reportsService';
import { formatCurrency } from '../../utils/formatters';

const T = {
  bg: '#faf7ee',
  card: '#ffffff',
  elevated: '#f4efe2',
  gold: '#d97706',
  goldLight: '#d97706',
  goldHover: '#b45309',
  textPrimary: '#1c2033',
  textSecondary: '#5b5a52',
  textMuted: '#6b6a5c',
  textDim: '#e3dcc4',
  border: 'rgba(92,78,51,0.15)',
  borderSubtle: 'rgba(92,78,51,0.25)',
  green: '#16a34a',
  blue: '#2563eb',
  red: '#dc2626',
  purple: '#7c3aed',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  background: '#f4efe2',
  border: `1px solid ${T.borderSubtle}`,
  borderRadius: 4,
  color: '#5b5a52',
  fontFamily: "'Inter', sans-serif",
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: T.textMuted,
  marginBottom: 8,
};

const sectionHeadingStyle = (color: string): React.CSSProperties => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: 16,
  fontWeight: 700,
  color,
  borderBottom: `1px solid ${T.border}`,
  paddingBottom: 10,
  marginBottom: 16,
  letterSpacing: '0.03em',
});

const rowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px 0',
  borderBottom: `1px solid ${T.border}`,
  fontFamily: "'Inter', sans-serif",
  fontSize: 13,
};

const totalRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px 0',
  fontFamily: "'Inter', sans-serif",
  fontSize: 13,
  fontWeight: 600,
  borderTop: `1px solid ${T.borderSubtle}`,
  marginTop: 4,
};

export const AdvancedAnalytics: React.FC = () => {
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: new Date().toISOString().split('T')[0]
  });
  const [activeAnalytic, setActiveAnalytic] = useState<'profitloss' | 'balancesheet' | 'cashflow' | 'trends'>('profitloss');
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);
  const [hoveredBtn, setHoveredBtn] = useState<string | null>(null);

  const analytics = [
    { id: 'profitloss', label: 'Profit & Loss', icon: '💰' },
    { id: 'balancesheet', label: 'Balance Sheet', icon: '⚖️' },
    { id: 'cashflow', label: 'Cash Flow', icon: '💧' },
    { id: 'trends', label: 'Trend Analysis', icon: '📈' }
  ];

  // Advanced analytics — real data from backend
  const { data: analyticsData, isLoading: analyticsLoading } = useQuery({
    queryKey: ['advanced-analytics', dateRange.startDate, dateRange.endDate],
    queryFn: () => reportsService.getAdvancedAnalytics({
      startDate: dateRange.startDate || undefined,
      endDate: dateRange.endDate || undefined,
    }),
  });

  // Cash flow — real data from backend
  const { data: cashFlow, isLoading: cashFlowLoading } = useQuery({
    queryKey: ['cash-flow-analytics', dateRange.startDate, dateRange.endDate],
    queryFn: () => reportsService.getCashFlowAnalysis({
      startDate: dateRange.startDate || undefined,
      endDate: dateRange.endDate || undefined,
    }),
  });

  const handleExport = (format: 'pdf' | 'excel') => {
    console.log(`Exporting ${activeAnalytic} as ${format}`);
  };

  const renderDateFilters = () => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
      <div>
        <label style={labelStyle}>From Date</label>
        <input
          type="date"
          value={dateRange.startDate}
          onChange={(e) => setDateRange(p => ({ ...p, startDate: e.target.value }))}
          style={inputStyle}
        />
      </div>
      <div>
        <label style={labelStyle}>To Date</label>
        <input
          type="date"
          value={dateRange.endDate}
          onChange={(e) => setDateRange(p => ({ ...p, endDate: e.target.value }))}
          style={inputStyle}
        />
      </div>
    </div>
  );

  // ── Profit & Loss ─────────────────────────────────────────────────────────────
  const renderProfitLossStatement = () => {
    const pl = analyticsData?.profitLoss;

    const totalRevenue = pl?.totalRevenue ?? 0;
    const cogs = totalRevenue - (pl?.grossProfit ?? 0);
    const operatingExp = pl?.operatingExpenses ?? 0;
    const adminExp = pl?.administrativeExpenses ?? 0;
    const marketingExp = pl?.marketingExpenses ?? 0;
    const totalExpenses = pl?.totalExpenses ?? 0;
    const netIncome = pl?.netIncome ?? 0;
    const profitMargin = pl?.profitMargin ?? 0;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {renderDateFilters()}

        <div style={{ background: T.elevated, border: `1px solid ${T.border}`, borderRadius: 6, padding: 24 }}>
          <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 700, color: T.textPrimary, marginTop: 0, marginBottom: 24, letterSpacing: '0.02em' }}>
            Profit &amp; Loss Statement
          </h3>

          {analyticsLoading ? (
            <div style={{ textAlign: 'center', color: T.textMuted, padding: 40, fontFamily: "'Inter', sans-serif", fontSize: 13 }}>Loading...</div>
          ) : (
            <>
              {/* Revenue */}
              <div style={{ marginBottom: 24 }}>
                <div style={sectionHeadingStyle(T.green)}>Revenue</div>
                <div style={rowStyle}>
                  <span style={{ color: T.textSecondary }}>Total Revenue</span>
                  <span style={{ color: T.green, fontWeight: 500 }}>{formatCurrency(totalRevenue)}</span>
                </div>
                <div style={totalRowStyle}>
                  <span style={{ color: T.textPrimary }}>Total Revenue</span>
                  <span style={{ color: T.green }}>{formatCurrency(totalRevenue)}</span>
                </div>
              </div>

              {/* Expenses */}
              <div style={{ marginBottom: 24 }}>
                <div style={sectionHeadingStyle(T.red)}>Expenses</div>
                {cogs > 0 && (
                  <div style={rowStyle}>
                    <span style={{ color: T.textSecondary }}>Cost of Goods Sold</span>
                    <span style={{ color: T.red, fontWeight: 500 }}>{formatCurrency(cogs)}</span>
                  </div>
                )}
                {operatingExp > 0 && (
                  <div style={rowStyle}>
                    <span style={{ color: T.textSecondary }}>Operating Expenses</span>
                    <span style={{ color: T.red, fontWeight: 500 }}>{formatCurrency(operatingExp)}</span>
                  </div>
                )}
                {adminExp > 0 && (
                  <div style={rowStyle}>
                    <span style={{ color: T.textSecondary }}>Administrative Expenses</span>
                    <span style={{ color: T.red, fontWeight: 500 }}>{formatCurrency(adminExp)}</span>
                  </div>
                )}
                {marketingExp > 0 && (
                  <div style={rowStyle}>
                    <span style={{ color: T.textSecondary }}>Marketing Expenses</span>
                    <span style={{ color: T.red, fontWeight: 500 }}>{formatCurrency(marketingExp)}</span>
                  </div>
                )}
                <div style={totalRowStyle}>
                  <span style={{ color: T.textPrimary }}>Total Expenses</span>
                  <span style={{ color: T.red }}>{formatCurrency(totalExpenses)}</span>
                </div>
              </div>

              {/* Net Income */}
              <div style={{ background: T.card, border: `1px solid ${T.borderSubtle}`, borderRadius: 6, padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 700, color: T.textPrimary }}>Net Income</span>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 28, fontWeight: 700, color: netIncome >= 0 ? T.green : T.red }}>
                    {formatCurrency(netIncome)}
                  </span>
                </div>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: T.textMuted, marginTop: 6 }}>
                  Profit Margin: {profitMargin.toFixed(1)}%
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // ── Balance Sheet ─────────────────────────────────────────────────────────────
  const renderBalanceSheet = () => {
    const bs = analyticsData?.balanceSheet;
    const assets = bs?.assets;
    const liabilities = bs?.liabilities;
    const equity = bs?.equity;

    const cashAndBank = assets?.currentAssets?.cashAndBank ?? 0;
    const accountsReceivable = assets?.currentAssets?.accountsReceivable ?? 0;
    const inventory = assets?.currentAssets?.inventory ?? 0;
    const totalCurrentAssets = cashAndBank + accountsReceivable + inventory;

    const equipment = assets?.fixedAssets?.equipment ?? 0;
    const vehicles = assets?.fixedAssets?.vehicles ?? 0;
    const totalFixedAssets = equipment + vehicles;
    const totalAssets = totalCurrentAssets + totalFixedAssets;

    const accountsPayable = liabilities?.currentLiabilities?.accountsPayable ?? 0;
    const shortTermLoans = liabilities?.currentLiabilities?.shortTermLoans ?? 0;
    const totalCurrentLiabilities = accountsPayable + shortTermLoans;

    const longTermLoans = liabilities?.longTermLiabilities?.longTermLoans ?? 0;
    const capital = equity?.capital ?? 0;
    const retainedEarnings = equity?.retainedEarnings ?? 0;
    const totalEquity = capital + retainedEarnings;
    const totalLiabilitiesAndEquity = totalCurrentLiabilities + longTermLoans + totalEquity;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {renderDateFilters()}

        <div style={{ background: T.elevated, border: `1px solid ${T.border}`, borderRadius: 6, padding: 24 }}>
          <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 700, color: T.textPrimary, marginTop: 0, marginBottom: 24, letterSpacing: '0.02em' }}>
            Balance Sheet
          </h3>

          {analyticsLoading ? (
            <div style={{ textAlign: 'center', color: T.textMuted, padding: 40, fontFamily: "'Inter', sans-serif", fontSize: 13 }}>Loading...</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
              {/* Assets */}
              <div>
                <div style={sectionHeadingStyle(T.blue)}>Assets</div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 600, color: T.textPrimary, marginBottom: 10, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Current Assets</div>
                  <div style={{ paddingLeft: 16 }}>
                    {([['Cash & Bank', cashAndBank], ['Accounts Receivable', accountsReceivable], ['Inventory', inventory]] as [string, number][])
                      .filter(([, v]) => v > 0)
                      .map(([label, val]) => (
                        <div key={label} style={rowStyle}>
                          <span style={{ color: T.textSecondary }}>{label}</span>
                          <span style={{ color: T.textPrimary, fontWeight: 500 }}>{formatCurrency(val)}</span>
                        </div>
                      ))}
                    <div style={totalRowStyle}>
                      <span style={{ color: T.textPrimary }}>Total Current Assets</span>
                      <span style={{ color: T.gold }}>{formatCurrency(totalCurrentAssets)}</span>
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 600, color: T.textPrimary, marginBottom: 10, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Fixed Assets</div>
                  <div style={{ paddingLeft: 16 }}>
                    {([['Equipment', equipment], ['Vehicles', vehicles]] as [string, number][])
                      .filter(([, v]) => v > 0)
                      .map(([label, val]) => (
                        <div key={label} style={rowStyle}>
                          <span style={{ color: T.textSecondary }}>{label}</span>
                          <span style={{ color: T.textPrimary, fontWeight: 500 }}>{formatCurrency(val)}</span>
                        </div>
                      ))}
                    <div style={totalRowStyle}>
                      <span style={{ color: T.textPrimary }}>Total Fixed Assets</span>
                      <span style={{ color: T.gold }}>{formatCurrency(totalFixedAssets)}</span>
                    </div>
                  </div>
                </div>

                <div style={{ background: T.card, border: `1px solid ${T.borderSubtle}`, borderRadius: 6, padding: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, fontWeight: 700, color: T.blue }}>Total Assets</span>
                    <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 700, color: T.gold }}>{formatCurrency(totalAssets)}</span>
                  </div>
                </div>
              </div>

              {/* Liabilities & Equity */}
              <div>
                <div style={sectionHeadingStyle(T.red)}>Liabilities &amp; Equity</div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 600, color: T.textPrimary, marginBottom: 10, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Current Liabilities</div>
                  <div style={{ paddingLeft: 16 }}>
                    {([['Accounts Payable', accountsPayable], ['Short-term Loans', shortTermLoans]] as [string, number][])
                      .filter(([, v]) => v > 0)
                      .map(([label, val]) => (
                        <div key={label} style={rowStyle}>
                          <span style={{ color: T.textSecondary }}>{label}</span>
                          <span style={{ color: T.textPrimary, fontWeight: 500 }}>{formatCurrency(val)}</span>
                        </div>
                      ))}
                    <div style={totalRowStyle}>
                      <span style={{ color: T.textPrimary }}>Total Current Liabilities</span>
                      <span style={{ color: T.red }}>{formatCurrency(totalCurrentLiabilities)}</span>
                    </div>
                  </div>
                </div>

                {longTermLoans > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 600, color: T.textPrimary, marginBottom: 10, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Long-term Liabilities</div>
                    <div style={{ paddingLeft: 16 }}>
                      <div style={rowStyle}>
                        <span style={{ color: T.textSecondary }}>Long-term Loans</span>
                        <span style={{ color: T.textPrimary, fontWeight: 500 }}>{formatCurrency(longTermLoans)}</span>
                      </div>
                      <div style={totalRowStyle}>
                        <span style={{ color: T.textPrimary }}>Total Long-term Liabilities</span>
                        <span style={{ color: T.red }}>{formatCurrency(longTermLoans)}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 600, color: T.textPrimary, marginBottom: 10, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Owner's Equity</div>
                  <div style={{ paddingLeft: 16 }}>
                    {([['Capital', capital], ['Retained Earnings', retainedEarnings]] as [string, number][])
                      .map(([label, val]) => (
                        <div key={label} style={rowStyle}>
                          <span style={{ color: T.textSecondary }}>{label}</span>
                          <span style={{ color: T.textPrimary, fontWeight: 500 }}>{formatCurrency(val)}</span>
                        </div>
                      ))}
                    <div style={totalRowStyle}>
                      <span style={{ color: T.textPrimary }}>Total Equity</span>
                      <span style={{ color: T.gold }}>{formatCurrency(totalEquity)}</span>
                    </div>
                  </div>
                </div>

                <div style={{ background: T.card, border: `1px solid ${T.borderSubtle}`, borderRadius: 6, padding: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 700, color: T.red }}>Total Liabilities &amp; Equity</span>
                    <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 700, color: T.gold }}>{formatCurrency(totalLiabilitiesAndEquity)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Cash Flow Statement ───────────────────────────────────────────────────────
  const renderCashFlowStatement = () => {
    const summary = cashFlow?.summary;
    const monthly = (cashFlow?.monthlyData ?? []) as any[];

    const netCashFlow = summary?.netCashFlow ?? 0;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {renderDateFilters()}

        <div style={{ background: T.elevated, border: `1px solid ${T.border}`, borderRadius: 6, padding: 24 }}>
          <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 700, color: T.textPrimary, marginTop: 0, marginBottom: 24, letterSpacing: '0.02em' }}>
            Cash Flow Statement
          </h3>

          {cashFlowLoading ? (
            <div style={{ textAlign: 'center', color: T.textMuted, padding: 40, fontFamily: "'Inter', sans-serif", fontSize: 13 }}>Loading...</div>
          ) : (
            <>
              {/* Operating Activities */}
              <div style={{ marginBottom: 24 }}>
                <div style={sectionHeadingStyle(T.green)}>Operating Activities</div>
                <div style={rowStyle}>
                  <span style={{ color: T.textSecondary }}>Total Inflows (Received)</span>
                  <span style={{ color: T.green, fontWeight: 500 }}>{formatCurrency(summary?.totalInflows ?? 0)}</span>
                </div>
                <div style={rowStyle}>
                  <span style={{ color: T.textSecondary }}>Total Outflows (Paid)</span>
                  <span style={{ color: T.red, fontWeight: 500 }}>({formatCurrency(summary?.totalOutflows ?? 0)})</span>
                </div>
                <div style={totalRowStyle}>
                  <span style={{ color: T.textPrimary }}>Net Cash from Operations</span>
                  <span style={{ color: netCashFlow >= 0 ? T.green : T.red }}>
                    {netCashFlow < 0 ? `(${formatCurrency(Math.abs(netCashFlow))})` : formatCurrency(netCashFlow)}
                  </span>
                </div>
              </div>

              {/* Monthly Breakdown */}
              {monthly.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <div style={sectionHeadingStyle(T.blue)}>Monthly Breakdown</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {monthly.slice(-6).map((m, i) => (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 12, padding: '10px 0', borderBottom: `1px solid ${T.border}` }}>
                        <span style={{ color: T.textSecondary, fontFamily: "'Inter', sans-serif", fontSize: 13 }}>{m.month}</span>
                        <span style={{ color: T.green, fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 500 }}>{formatCurrency(m.inflows)}</span>
                        <span style={{ color: T.red, fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 500 }}>({formatCurrency(m.outflows)})</span>
                        <span style={{ color: m.netFlow >= 0 ? T.blue : T.red, fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600 }}>
                          {m.netFlow < 0 ? `(${formatCurrency(Math.abs(m.netFlow))})` : formatCurrency(m.netFlow)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary box */}
              <div style={{ background: T.card, border: `1px solid ${T.borderSubtle}`, borderRadius: 6, padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 700, color: T.textPrimary }}>Net Cash Flow</span>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 24, fontWeight: 700, color: netCashFlow >= 0 ? T.green : T.red }}>
                    {netCashFlow < 0 ? `(${formatCurrency(Math.abs(netCashFlow))})` : formatCurrency(netCashFlow)}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, paddingTop: 10, borderTop: `1px solid ${T.borderSubtle}` }}>
                  <div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.textMuted, marginBottom: 4 }}>Collection Rate</div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 700, color: T.blue }}>{(summary?.collectionRate ?? 0).toFixed(1)}%</div>
                  </div>
                  <div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.textMuted, marginBottom: 4 }}>Payment Rate</div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 700, color: T.purple }}>{(summary?.paymentRate ?? 0).toFixed(1)}%</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // ── Trend Analysis ────────────────────────────────────────────────────────────
  const renderTrendAnalysis = () => {
    const kpis = analyticsData?.kpis;
    const trends = (analyticsData?.monthlyTrends ?? []) as any[];
    const topJobs = (analyticsData?.topJobs ?? []) as any[];
    const pl = analyticsData?.profitLoss;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {renderDateFilters()}

        {/* KPI Cards — real data */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          {[
            {
              label: 'Revenue Growth',
              value: kpis ? `${(kpis.revenueGrowthRate ?? 0) >= 0 ? '+' : ''}${(kpis.revenueGrowthRate ?? 0).toFixed(1)}%` : '—',
              sub: 'vs last period',
              color: (kpis?.revenueGrowthRate ?? 0) >= 0 ? T.green : T.red,
              border: (kpis?.revenueGrowthRate ?? 0) >= 0 ? T.green : T.red,
            },
            {
              label: 'Profit Margin',
              value: pl ? `${(pl.profitMargin ?? 0).toFixed(1)}%` : '—',
              sub: 'net income / revenue',
              color: T.blue,
              border: T.blue,
            },
            {
              label: 'Total Customers',
              value: kpis ? String(kpis.totalCustomers ?? 0) : '—',
              sub: `${kpis?.totalVendors ?? 0} vendors`,
              color: T.purple,
              border: T.purple,
            },
            {
              label: 'Collection Period',
              value: kpis ? `${(kpis.collectionPeriod ?? 0).toFixed(1)} days` : '—',
              sub: 'avg days to collect',
              color: T.gold,
              border: T.gold,
            },
          ].map((card) => (
            <div key={card.label} style={{ background: T.card, border: `1px solid ${T.border}`, borderLeft: `3px solid ${card.border}`, borderRadius: 6, padding: 20 }}>
              <div style={labelStyle}>{card.label}</div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 26, fontWeight: 700, color: card.color }}>
                {analyticsLoading ? '—' : card.value}
              </div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: T.textMuted, marginTop: 4 }}>{card.sub}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          {/* Monthly Revenue Trends — real */}
          <div style={{ background: T.elevated, border: `1px solid ${T.border}`, borderRadius: 6, padding: 24 }}>
            <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 700, color: T.textPrimary, marginTop: 0, marginBottom: 16 }}>
              Monthly Revenue Trend
            </h3>
            {analyticsLoading ? (
              <div style={{ textAlign: 'center', color: T.textMuted, padding: 24, fontFamily: "'Inter', sans-serif", fontSize: 13 }}>Loading...</div>
            ) : trends.length === 0 ? (
              <div style={{ textAlign: 'center', color: T.textMuted, padding: 24, fontFamily: "'Inter', sans-serif", fontSize: 13 }}>No trend data available.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {trends.slice(-8).map((t, i) => {
                  const growth = t.growth ?? t.revenueGrowth ?? null;
                  const revenue = t.revenue ?? t.totalRevenue ?? 0;
                  return (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: T.card, borderRadius: 4, border: `1px solid ${T.border}` }}>
                      <div>
                        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 500, color: T.textPrimary }}>{t.month ?? t.period ?? `Period ${i + 1}`}</div>
                        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: T.textMuted }}>{formatCurrency(revenue)}</div>
                      </div>
                      {growth !== null && (
                        <div style={{ padding: '3px 8px', borderRadius: 4, background: growth >= 0 ? 'rgba(22,163,74,0.1)' : 'rgba(220,38,38,0.1)', color: growth >= 0 ? T.green : T.red, fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 600 }}>
                          {growth >= 0 ? '+' : ''}{Number(growth).toFixed(1)}%
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Top Jobs — real */}
          <div style={{ background: T.elevated, border: `1px solid ${T.border}`, borderRadius: 6, padding: 24 }}>
            <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 700, color: T.textPrimary, marginTop: 0, marginBottom: 16 }}>
              Top Jobs by Revenue
            </h3>
            {analyticsLoading ? (
              <div style={{ textAlign: 'center', color: T.textMuted, padding: 24, fontFamily: "'Inter', sans-serif", fontSize: 13 }}>Loading...</div>
            ) : topJobs.length === 0 ? (
              <div style={{ textAlign: 'center', color: T.textMuted, padding: 24, fontFamily: "'Inter', sans-serif", fontSize: 13 }}>No job data available.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {topJobs.slice(0, 6).map((job, i) => (
                  <div key={i} style={{ padding: '10px 12px', background: T.card, borderRadius: 4, border: `1px solid ${T.border}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 500, color: T.textPrimary }}>{job.jobNumber}</div>
                        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: T.textMuted, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.description}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 700, color: T.green }}>{formatCurrency(job.revenue ?? 0)}</div>
                        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: job.profit >= 0 ? T.blue : T.red }}>
                          Profit: {formatCurrency(job.profit ?? 0)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeAnalytic) {
      case 'profitloss': return renderProfitLossStatement();
      case 'balancesheet': return renderBalanceSheet();
      case 'cashflow': return renderCashFlowStatement();
      case 'trends': return renderTrendAnalysis();
      default: return renderProfitLossStatement();
    }
  };

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontFamily: "'Inter', sans-serif", fontSize: 26, fontWeight: 700, color: T.textPrimary, margin: 0, letterSpacing: '0.02em' }}>
          Advanced Analytics
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => handleExport('excel')}
            onMouseEnter={() => setHoveredBtn('excel')}
            onMouseLeave={() => setHoveredBtn(null)}
            style={{ padding: '8px 16px', background: hoveredBtn === 'excel' ? 'rgba(22,163,74,0.15)' : 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.25)', borderRadius: 4, color: T.green, fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', cursor: 'pointer', transition: 'background 0.2s' }}
          >Export Excel</button>
          <button
            onClick={() => handleExport('pdf')}
            onMouseEnter={() => setHoveredBtn('pdf')}
            onMouseLeave={() => setHoveredBtn(null)}
            style={{ padding: '8px 16px', background: hoveredBtn === 'pdf' ? 'rgba(220,38,38,0.15)' : 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: 4, color: T.red, fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', cursor: 'pointer', transition: 'background 0.2s' }}
          >Export PDF</button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{ borderBottom: `1px solid ${T.border}`, marginBottom: 24 }}>
        <nav style={{ display: 'flex', gap: 0 }}>
          {analytics.map((analytic) => {
            const isActive = activeAnalytic === analytic.id;
            const isHovered = hoveredTab === analytic.id;
            return (
              <button
                key={analytic.id}
                onClick={() => setActiveAnalytic(analytic.id as any)}
                onMouseEnter={() => setHoveredTab(analytic.id)}
                onMouseLeave={() => setHoveredTab(null)}
                style={{
                  padding: '10px 20px', border: 'none',
                  borderBottom: isActive ? `2px solid ${T.gold}` : '2px solid transparent',
                  background: 'transparent', cursor: 'pointer',
                  fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 600,
                  letterSpacing: '0.22em', textTransform: 'uppercase',
                  color: isActive ? T.gold : isHovered ? T.textSecondary : T.textMuted,
                  whiteSpace: 'nowrap', transition: 'color 0.2s, border-color 0.2s',
                  marginBottom: -1, display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                <span style={{ fontSize: 13 }}>{analytic.icon}</span>
                {analytic.label}
              </button>
            );
          })}
        </nav>
      </div>

      {renderContent()}
    </div>
  );
};
